'use client';

/**
 * Organizations tab — merchant orgs (Clerk orgs) with member CRUD.
 *
 * Mounted from two places, with the SAME component (no scope-aware
 * fork on the client):
 *   - `/admin/iam` Organizations tab → superadmin / payment_ops /
 *     internal AM see every merchant.
 *   - `/partner-admin/:partner/team` Merchants tab → partneradmin
 *     and partner-scoped AM see ONLY their partner's merchants.
 *
 * The scope difference is enforced server-side: `iamOrgs.list`
 * delegates to the `listOrgs` use-case which auto-clamps the query
 * by `actor.partnerScope` (partner-scoped callers cannot bypass
 * their attribution by passing a foreign `partnerSlug`). That means
 * this component can stay scope-agnostic — it asks for "all orgs",
 * the backend serves the slice the caller is allowed to see.
 *
 * Permissions model (mirrors the backend `canManageOrgMembership`)
 * ----------------------------------------------------------------
 * - superadmin / payment_ops / tagadapaymentAdmin → any org, full CRUD.
 * - partneradmin scoped to X                   → only orgs of X.
 * - accountmanager (no scope = internal AM)    → any org.
 * - accountmanager scoped to X                 → only orgs of X.
 * - everyone else                              → read-only list.
 *
 * The UI only HIDES the actions a user shouldn't see; the server
 * still enforces the rules via the IAM use-case so a hand-crafted
 * request can't bypass them.
 */
import * as React from 'react';
import {
  AlertCircle,
  Building2,
  ChevronLeft,
  ChevronRight,
  Handshake,
  Loader2,
  Search,
  ShieldAlert,
  Trash2,
  UserPlus,
  Users,
  X,
} from 'lucide-react';
import toast from 'react-hot-toast';

import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { IamErrorAlert } from '@/lib/iam/IamErrorAlert';
import {
  toIamFriendlyError,
  type IamFriendlyError,
} from '@/lib/iam/friendly-errors';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { Skeleton } from '@/components/ui/skeleton';
import { ChangePartnerDialog } from '@/components/iam/ChangePartnerDialog';
import { useSessionClaims } from '@/hooks/use-session-claims';
import { trpc } from '@/lib/api/trpc';

const PAGE_SIZE = 50;
const PARTNER_ANY = '__any__';
const PARTNER_NONE = '__none__';

type OrgRow = {
  accountId: string;
  orgClerkId: string;
  orgClerkName: string | null;
  orgClerkSlug: string | null;
  kind: 'self_serve' | 'partner_managed';
  createdAt: Date | string | null;
  partnerId: string | null;
  partnerSlug: string | null;
  partnerName: string | null;
};

type MembershipRow = {
  membershipId: string;
  clerkUserId: string;
  email: string | null;
  firstName: string | null;
  lastName: string | null;
  imageUrl: string | null;
  role: string;
  createdAt: Date | string | null;
};

function fullName(m: { firstName: string | null; lastName: string | null }) {
  const n = [m.firstName, m.lastName].filter(Boolean).join(' ').trim();
  return n.length > 0 ? n : null;
}

function initials(m: MembershipRow): string {
  const f = m.firstName?.[0] ?? '';
  const l = m.lastName?.[0] ?? '';
  if (f || l) return (f + l).toUpperCase();
  return (m.email?.[0] ?? '?').toUpperCase();
}

/**
 * Coarse "can this user mutate any org membership" signal. Mirrors
 * `canManageOrgMembership` but without the per-org scope check
 * (that runs per row when we render the side sheet).
 */
function useCanManageMemberships() {
  const { isSuperAdmin, isPartnerAdmin, whitelabelScope, tgdRoles } =
    useSessionClaims();
  const isHubAdminOrTpa =
    tgdRoles.includes('payment_ops') ||
    tgdRoles.includes('tagadapaymentAdmin');
  const isAccountManager = tgdRoles.includes('accountmanager');
  const canMutateAnything =
    isSuperAdmin || isHubAdminOrTpa || isPartnerAdmin || isAccountManager;
  // Per-org scope check used in the side sheet: scoped users only
  // see Add / Remove buttons on orgs of their own partner.
  const canMutateOrg = React.useCallback(
    (org: { partnerSlug: string | null }) => {
      if (isSuperAdmin || isHubAdminOrTpa) return true;
      if (isAccountManager && !whitelabelScope) return true; // internal AM
      if (
        (isAccountManager || isPartnerAdmin) &&
        whitelabelScope &&
        org.partnerSlug === whitelabelScope
      )
        return true;
      return false;
    },
    [isSuperAdmin, isHubAdminOrTpa, isPartnerAdmin, isAccountManager, whitelabelScope],
  );
  return { canMutateAnything, canMutateOrg };
}

export function OrgsTab() {
  const { isSignedIn, whitelabelScope, isSuperAdmin } = useSessionClaims();
  const { canMutateAnything } = useCanManageMemberships();
  // Partner-scoped users (partneradmin, scoped AM) only ever see
  // their partner's orgs (the backend clamps regardless of input),
  // so showing a partner picker is misleading. We hide it for them
  // and keep it for cross-partner admins.
  const isPartnerScoped = whitelabelScope !== null;

  const [q, setQ] = React.useState('');
  const [qDebounced, setQDebounced] = React.useState('');
  const [partnerFilter, setPartnerFilter] = React.useState(PARTNER_ANY);
  const [page, setPage] = React.useState(0);
  const [selected, setSelected] = React.useState<OrgRow | null>(null);

  React.useEffect(() => {
    const t = setTimeout(() => setQDebounced(q), 250);
    return () => clearTimeout(t);
  }, [q]);
  React.useEffect(() => {
    setPage(0);
  }, [qDebounced, partnerFilter]);

  const partnersQuery = trpc.partners.listActivePartnerSlugs.useQuery(
    undefined,
    { enabled: isSignedIn && !isPartnerScoped, staleTime: 5 * 60 * 1000 },
  );
  const partners = partnersQuery.data ?? [];

  const orgsQuery = trpc.iamOrgs.list.useQuery(
    {
      q: qDebounced || undefined,
      partnerSlug:
        partnerFilter === PARTNER_ANY || partnerFilter === PARTNER_NONE
          ? undefined
          : partnerFilter,
      partnerId: partnerFilter === PARTNER_NONE ? '__none__' : undefined,
      limit: PAGE_SIZE,
      offset: page * PAGE_SIZE,
    },
    { enabled: isSignedIn, staleTime: 15_000 },
  );

  const total = orgsQuery.data?.total ?? 0;
  const rows = (orgsQuery.data?.rows ?? []) as OrgRow[];
  const hasFilter = !!qDebounced || partnerFilter !== PARTNER_ANY;

  return (
    <div className="space-y-5">
      <header className="space-y-1">
        <h2 className="text-lg font-semibold tracking-tight">
          Merchant organisations
        </h2>
        <p className="max-w-3xl text-sm text-muted-foreground">
          {canMutateAnything
            ? isSuperAdmin
              ? 'Click an organisation to manage members or reassign its partner. Partner changes fan out to Clerk, Postgres, Stripe, and ClickHouse — preview before applying.'
              : 'Click an organisation to view, add, or remove members. Replaces the Clerk dashboard for merchant-org membership management.'
            : 'Browse merchant organisations attached to each partner. Read-only, ask an account manager or partner admin to change membership.'}
        </p>
      </header>

      <section
        aria-label="Filters"
        className="flex flex-wrap items-center gap-2"
      >
        <div className="relative min-w-0 flex-1 sm:max-w-sm">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Search by name, slug, or account id…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="h-9 pl-9 pr-8"
          />
          {q && (
            <button
              type="button"
              onClick={() => setQ('')}
              className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-0.5 text-muted-foreground hover:bg-muted hover:text-foreground"
              aria-label="Clear search"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
        {!isPartnerScoped && (
          <Select value={partnerFilter} onValueChange={setPartnerFilter}>
            <SelectTrigger className="h-9 w-[200px]">
              <SelectValue placeholder="Any partner" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={PARTNER_ANY}>Any partner</SelectItem>
              <SelectItem value={PARTNER_NONE}>Unaffiliated only</SelectItem>
              {partners.map((p: { slug: string; name: string }) => (
                <SelectItem key={p.slug} value={p.slug}>
                  {p.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
        {hasFilter && (
          <Button
            variant="ghost"
            size="sm"
            className="h-9 text-muted-foreground hover:text-foreground"
            onClick={() => {
              setQ('');
              setPartnerFilter(PARTNER_ANY);
            }}
          >
            <X className="mr-1.5 h-3.5 w-3.5" /> Clear
          </Button>
        )}
      </section>

      <section aria-label="Organisations" className="space-y-3">
        {orgsQuery.isLoading ? (
          <div className="overflow-hidden rounded-lg border bg-card">
            {Array.from({ length: 6 }).map((_, i) => (
              <div
                key={i}
                className="flex items-center gap-4 border-b px-4 py-3 last:border-0"
              >
                <div className="flex-1 space-y-1.5">
                  <Skeleton className="h-3.5 w-48" />
                  <Skeleton className="h-3 w-32" />
                </div>
                <Skeleton className="h-5 w-20" />
                <Skeleton className="hidden h-3 w-24 md:block" />
                <Skeleton className="h-7 w-20" />
              </div>
            ))}
          </div>
        ) : orgsQuery.error ? (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>Failed to load organisations</AlertTitle>
            <AlertDescription>{orgsQuery.error.message}</AlertDescription>
          </Alert>
        ) : rows.length === 0 ? (
          <div className="rounded-lg border border-dashed bg-card/40 px-6 py-14 text-center">
            <Building2 className="mx-auto h-9 w-9 text-muted-foreground/60" />
            <h3 className="mt-3 text-sm font-medium">No organisations</h3>
            <p className="mt-1.5 text-sm text-muted-foreground">
              {hasFilter
                ? 'Try a broader search or clear the partner filter.'
                : 'No merchants have been onboarded yet.'}
            </p>
          </div>
        ) : (
          <>
            <div className="overflow-hidden rounded-lg border bg-card">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b bg-muted/40 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                    <th className="px-4 py-2.5 text-left">Organisation</th>
                    <th className="px-4 py-2.5 text-left">Partner</th>
                    <th className="hidden px-4 py-2.5 text-left md:table-cell">
                      Kind
                    </th>
                    <th className="hidden px-4 py-2.5 text-left md:table-cell">
                      Created
                    </th>
                    <th className="w-px px-4 py-2.5" />
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {rows.map((o) => (
                    <tr
                      key={o.accountId}
                      onClick={() => setSelected(o)}
                      className={
                        'cursor-pointer transition-colors hover:bg-muted/30 ' +
                        (selected?.accountId === o.accountId
                          ? 'bg-muted/50'
                          : '')
                      }
                    >
                      <td className="max-w-0 px-4 py-3">
                        <div className="truncate font-medium text-foreground">
                          {o.orgClerkName || (
                            <span className="text-muted-foreground">—</span>
                          )}
                        </div>
                        <div className="mt-0.5 truncate font-mono text-[11px] text-muted-foreground">
                          {o.orgClerkSlug ?? o.accountId}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        {o.partnerName ? (
                          <Badge
                            variant="secondary"
                            className="px-1.5 py-0 text-[10px] font-medium"
                          >
                            {o.partnerName}
                          </Badge>
                        ) : (
                          <span className="text-xs text-muted-foreground">
                            unaffiliated
                          </span>
                        )}
                      </td>
                      <td className="hidden px-4 py-3 text-xs text-muted-foreground md:table-cell">
                        {o.kind === 'self_serve'
                          ? 'self-serve'
                          : 'partner-managed'}
                      </td>
                      <td className="hidden whitespace-nowrap px-4 py-3 text-xs text-muted-foreground md:table-cell">
                        {o.createdAt
                          ? new Date(o.createdAt).toLocaleDateString()
                          : '—'}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-7 px-2 text-xs"
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelected(o);
                          }}
                        >
                          <Users className="mr-1.5 h-3.5 w-3.5" /> Members
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="flex items-center justify-between text-xs text-muted-foreground">
              <div className="tabular-nums">
                <span className="font-medium text-foreground">
                  {page * PAGE_SIZE + 1}
                </span>
                <span className="mx-1">–</span>
                <span className="font-medium text-foreground">
                  {Math.min(total, (page + 1) * PAGE_SIZE)}
                </span>
                <span className="mx-1">of</span>
                <span className="font-medium text-foreground">
                  {total.toLocaleString()}
                </span>
              </div>
              <div className="flex items-center gap-1">
                <Button
                  size="icon"
                  variant="ghost"
                  className="h-8 w-8"
                  disabled={page === 0 || orgsQuery.isFetching}
                  onClick={() => setPage((p) => Math.max(0, p - 1))}
                  aria-label="Previous page"
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <Button
                  size="icon"
                  variant="ghost"
                  className="h-8 w-8"
                  disabled={
                    (page + 1) * PAGE_SIZE >= total || orgsQuery.isFetching
                  }
                  onClick={() => setPage((p) => p + 1)}
                  aria-label="Next page"
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </>
        )}
      </section>

      <OrgMembersSheet
        org={selected}
        open={selected !== null}
        onOpenChange={(o) => !o && setSelected(null)}
        onPartnerChanged={(patch) => {
          setSelected((prev) => (prev ? { ...prev, ...patch } : prev));
        }}
      />
    </div>
  );
}

/**
 * Side sheet listing the members of a single Clerk org. Inlines the
 * Add-member dialog and per-row Role select / Remove button.
 *
 * The sheet always opens read-only first; mutating buttons appear
 * only when `canMutateOrg(org)` returns true. We rely on the server
 * to do the real check — if a malicious caller forges a request,
 * they get a 403 from the use-case, not a soft client-side block.
 */
function OrgMembersSheet({
  org,
  open,
  onOpenChange,
  onPartnerChanged,
}: {
  org: OrgRow | null;
  open: boolean;
  onOpenChange: (o: boolean) => void;
  onPartnerChanged: (patch: {
    partnerSlug: string | null;
    partnerName: string | null;
    partnerId: string | null;
  }) => void;
}) {
  const { canMutateOrg } = useCanManageMemberships();
  const { isSuperAdmin } = useSessionClaims();
  const utils = trpc.useUtils();
  const [addOpen, setAddOpen] = React.useState(false);
  const [changePartnerOpen, setChangePartnerOpen] = React.useState(false);
  // Persistent error banner — replaces transient `toast.error`s for
  // permission failures (403 / IAM rejections). Toasts for permission
  // problems are too easy to miss; the user must see exactly which
  // policy blocked them and what to do about it. Cleared on close,
  // on a new mutation, or via the alert's dismiss button.
  const [lastError, setLastError] =
    React.useState<IamFriendlyError | null>(null);

  React.useEffect(() => {
    if (!open) setLastError(null);
  }, [open]);

  const membersQuery = trpc.iamOrgs.membersList.useQuery(
    org ? { clerkOrgId: org.orgClerkId } : ({ clerkOrgId: 'org_x' } as const),
    {
      enabled: open && !!org,
      staleTime: 5_000,
    },
  );
  const rolesQuery = trpc.iamOrgs.membersListRoles.useQuery(undefined, {
    enabled: open,
    staleTime: 60 * 60 * 1000,
  });
  const partnersForChangeQuery = trpc.partners.listActivePartnerSlugs.useQuery(
    undefined,
    { enabled: open && isSuperAdmin, staleTime: 5 * 60 * 1000 },
  );

  const setRoleMutation = trpc.iamOrgs.membersSetRole.useMutation({
    onMutate: () => setLastError(null),
    onSuccess: () => {
      toast.success('Role updated');
      if (org)
        utils.iamOrgs.membersList.invalidate({ clerkOrgId: org.orgClerkId });
    },
    onError: (e) => setLastError(toIamFriendlyError(e)),
  });
  const removeMutation = trpc.iamOrgs.membersRemove.useMutation({
    onMutate: () => setLastError(null),
    onSuccess: (res) => {
      if (res.wasNoop) toast('User was already not a member');
      else toast.success('Member removed');
      if (org)
        utils.iamOrgs.membersList.invalidate({ clerkOrgId: org.orgClerkId });
    },
    onError: (e) => setLastError(toIamFriendlyError(e)),
  });

  if (!org) return null;

  const canMutate = canMutateOrg(org);
  const members = (membersQuery.data?.rows ?? []) as MembershipRow[];
  const availableRoles = rolesQuery.data?.roles ?? ['org:admin', 'org:member'];

  // Layout note (matches the User edit sheet shell in `page.tsx`):
  //   SheetContent → strip default gap/padding so we own the rhythm
  //   SheetHeader  → bordered, generous top padding, tight bottom
  //   body         → flex-1 + overflow scroll + uniform horizontal pad
  //   SheetFooter  → sticky-ish bottom, muted bg, just a Close button
  //                  (no save/cancel flow — actions are inline)
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="flex w-full flex-col gap-0 p-0 sm:max-w-xl">
        <SheetHeader className="border-b px-6 pb-5 pt-6 pr-12">
          <div className="flex items-start gap-3">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md border bg-muted/40">
              <Building2 className="h-5 w-5 text-muted-foreground" />
            </div>
            <div className="min-w-0 flex-1 space-y-1">
              <SheetTitle className="truncate text-base leading-tight">
                {org.orgClerkName ?? org.orgClerkSlug ?? org.orgClerkId}
              </SheetTitle>
              <SheetDescription className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs">
                {org.partnerName ? (
                  <Badge
                    variant="secondary"
                    className="px-1.5 py-0 text-[10px] font-medium"
                  >
                    {org.partnerName}
                  </Badge>
                ) : (
                  <span className="text-muted-foreground">
                    Unaffiliated merchant
                  </span>
                )}
                <span className="font-mono text-[10px] text-muted-foreground/80">
                  {org.orgClerkId}
                </span>
              </SheetDescription>
            </div>
          </div>
        </SheetHeader>

        <div className="flex-1 space-y-5 overflow-y-auto px-6 py-6">
          {isSuperAdmin && (
            <section className="space-y-2 rounded-md border bg-muted/20 p-3">
              <div className="flex items-center justify-between gap-2">
                <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                  Partner attribution
                </h3>
                <Button
                  size="sm"
                  variant="outline"
                  className="h-7 gap-1.5 text-xs"
                  onClick={() => setChangePartnerOpen(true)}
                >
                  <Handshake className="h-3.5 w-3.5" />
                  Change partner
                </Button>
              </div>
              <p className="text-xs text-muted-foreground">
                {org.partnerName
                  ? `Affiliated with ${org.partnerName}. Reassignment updates billing routing, dashboards, and historical analytics.`
                  : 'Unaffiliated merchant (direct Tagada signup). Assign a partner to attach them to a whitelabel programme.'}
              </p>
            </section>
          )}

          <div className="flex items-center justify-between gap-3">
            <div className="flex items-baseline gap-2">
              <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                Members
              </h3>
              {membersQuery.data ? (
                <span className="text-xs tabular-nums text-muted-foreground/70">
                  {membersQuery.data.total}
                </span>
              ) : null}
            </div>
            {canMutate && (
              <Button
                size="sm"
                variant="default"
                className="h-8"
                onClick={() => setAddOpen(true)}
              >
                <UserPlus className="mr-1.5 h-3.5 w-3.5" /> Add member
              </Button>
            )}
          </div>

          {!canMutate && (
            <Alert>
              <ShieldAlert className="h-4 w-4" />
              <AlertTitle>Read-only view</AlertTitle>
              <AlertDescription>
                You can see the membership of this organisation but not
                change it. Account managers and partner admins scoped to
                this partner can add or remove members.
              </AlertDescription>
            </Alert>
          )}

          {lastError && (
            <IamErrorAlert
              error={lastError}
              onDismiss={() => setLastError(null)}
            />
          )}

          {membersQuery.isLoading ? (
            <div className="divide-y rounded-md border bg-card">
              {Array.from({ length: 3 }).map((_, i) => (
                <div key={i} className="flex items-center gap-3 px-4 py-3.5">
                  <Skeleton className="h-9 w-9 shrink-0 rounded-full" />
                  <div className="flex-1 space-y-1.5">
                    <Skeleton className="h-3.5 w-40" />
                    <Skeleton className="h-3 w-56" />
                  </div>
                  <Skeleton className="h-8 w-[136px]" />
                </div>
              ))}
            </div>
          ) : membersQuery.error ? (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertTitle>Failed to load members</AlertTitle>
              <AlertDescription>
                {membersQuery.error.message}
              </AlertDescription>
            </Alert>
          ) : members.length === 0 ? (
            <div className="rounded-md border border-dashed bg-card/40 px-6 py-12 text-center">
              <Users className="mx-auto h-8 w-8 text-muted-foreground/60" />
              <p className="mt-3 text-sm font-medium">No members yet</p>
              <p className="mt-1 text-xs text-muted-foreground">
                {canMutate
                  ? 'Use Add member to attach an existing Tagada user or invite a new email.'
                  : 'This organisation has no members.'}
              </p>
            </div>
          ) : (
            <ul className="divide-y rounded-md border bg-card">
              {members.map((m) => {
                const removing =
                  removeMutation.isPending &&
                  removeMutation.variables?.clerkUserId === m.clerkUserId;
                const updatingRole =
                  setRoleMutation.isPending &&
                  setRoleMutation.variables?.clerkUserId === m.clerkUserId;
                return (
                  <li
                    key={m.membershipId}
                    className="flex items-center gap-3 px-4 py-3"
                  >
                    <Avatar className="h-9 w-9 shrink-0">
                      <AvatarImage src={m.imageUrl ?? undefined} alt="" />
                      <AvatarFallback className="text-xs">
                        {initials(m)}
                      </AvatarFallback>
                    </Avatar>
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-sm font-medium leading-tight">
                        {fullName(m) ?? m.email ?? m.clerkUserId}
                      </div>
                      {fullName(m) && m.email && (
                        <div className="mt-0.5 truncate text-xs text-muted-foreground">
                          {m.email}
                        </div>
                      )}
                    </div>
                    {canMutate ? (
                      <>
                        <Select
                          value={m.role}
                          onValueChange={(v) => {
                            if (v === m.role) return;
                            setRoleMutation.mutate({
                              clerkOrgId: org.orgClerkId,
                              clerkUserId: m.clerkUserId,
                              role: v as 'org:admin' | 'org:member',
                            });
                          }}
                          disabled={updatingRole}
                        >
                          <SelectTrigger className="h-8 w-[136px] gap-1 text-xs">
                            {updatingRole ? (
                              <Loader2 className="h-3 w-3 animate-spin text-muted-foreground" />
                            ) : null}
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent align="end">
                            {availableRoles.map((r) => (
                              <SelectItem
                                key={r}
                                value={r}
                                className="text-xs"
                              >
                                {r}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <Button
                          size="icon"
                          variant="ghost"
                          className="h-8 w-8 shrink-0 text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
                          onClick={() => {
                            if (
                              !confirm(
                                `Remove ${fullName(m) ?? m.email ?? m.clerkUserId} from this organisation?`,
                              )
                            )
                              return;
                            removeMutation.mutate({
                              clerkOrgId: org.orgClerkId,
                              clerkUserId: m.clerkUserId,
                            });
                          }}
                          disabled={removing}
                          aria-label="Remove member"
                        >
                          {removing ? (
                            <Loader2 className="h-3.5 w-3.5 animate-spin" />
                          ) : (
                            <Trash2 className="h-3.5 w-3.5" />
                          )}
                        </Button>
                      </>
                    ) : (
                      <Badge
                        variant="outline"
                        className="font-mono text-[10px]"
                      >
                        {m.role}
                      </Badge>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        <SheetFooter className="border-t bg-muted/30 px-6 py-3 sm:flex-row sm:justify-end">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onOpenChange(false)}
          >
            Close
          </Button>
        </SheetFooter>

        <AddMemberDialog
          org={org}
          open={addOpen}
          onOpenChange={setAddOpen}
          availableRoles={availableRoles}
          onAdded={() => {
            utils.iamOrgs.membersList.invalidate({
              clerkOrgId: org.orgClerkId,
            });
          }}
        />

        {isSuperAdmin && (
          <ChangePartnerDialog
            org={org}
            open={changePartnerOpen}
            onOpenChange={setChangePartnerOpen}
            partners={partnersForChangeQuery.data ?? []}
            onApplied={({ partnerSlug, partnerName }) => {
              onPartnerChanged({
                partnerSlug,
                partnerName,
                partnerId: null,
              });
            }}
          />
        )}
      </SheetContent>
    </Sheet>
  );
}

function AddMemberDialog({
  org,
  open,
  onOpenChange,
  availableRoles,
  onAdded,
}: {
  org: OrgRow;
  open: boolean;
  onOpenChange: (o: boolean) => void;
  availableRoles: readonly string[];
  onAdded: () => void;
}) {
  const [email, setEmail] = React.useState('');
  const [role, setRole] = React.useState<string>(availableRoles[0] ?? 'org:member');
  // Local persistent error banner — see OrgMembersSheet for rationale.
  // We keep one per dialog to scope the alert to the form the user
  // is actually filling out.
  const [lastError, setLastError] =
    React.useState<IamFriendlyError | null>(null);

  React.useEffect(() => {
    if (!open) {
      setEmail('');
      setRole(availableRoles[0] ?? 'org:member');
      setLastError(null);
    }
  }, [open, availableRoles]);

  const addMutation = trpc.iamOrgs.membersAdd.useMutation({
    onMutate: () => setLastError(null),
    onSuccess: (res) => {
      if (res.alreadyMember) {
        toast(
          `User is already a member with role ${res.role}. Use the role picker to change it.`,
        );
      } else if (res.invited) {
        // The recipient doesn't have a Tagada account yet — Clerk
        // emailed them a sign-up + auto-join link. They'll appear
        // in the member list once they accept.
        toast.success(
          `Invitation sent to ${email.trim()}. They'll appear in the member list once they accept the email.`,
          { duration: 6_000 },
        );
      } else {
        toast.success('Member added');
      }
      onAdded();
      onOpenChange(false);
    },
    onError: (e) => setLastError(toIamFriendlyError(e)),
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader className="space-y-2">
          <DialogTitle className="text-base">
            Add member to{' '}
            <span className="text-muted-foreground">
              {org.orgClerkName ?? 'org'}
            </span>
          </DialogTitle>
          <DialogDescription className="text-xs leading-relaxed">
            If the email already belongs to a Tagada user, they're added
            instantly with the role you pick. Otherwise we send them a
            Clerk invitation email; they sign up via the link and land
            in this org with that role.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 pt-1">
          <div className="space-y-1.5">
            <Label htmlFor="add-member-email" className="text-xs font-medium">
              User email
            </Label>
            <Input
              id="add-member-email"
              type="email"
              placeholder="user@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoFocus
              className="h-9"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="add-member-role" className="text-xs font-medium">
              Role
            </Label>
            <Select value={role} onValueChange={setRole}>
              <SelectTrigger id="add-member-role" className="h-9">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {availableRoles.map((r) => (
                  <SelectItem key={r} value={r}>
                    {r}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {lastError && (
            <IamErrorAlert
              error={lastError}
              onDismiss={() => setLastError(null)}
            />
          )}
        </div>
        <DialogFooter className="gap-2 sm:gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onOpenChange(false)}
            disabled={addMutation.isPending}
          >
            Cancel
          </Button>
          <Button
            size="sm"
            onClick={() => {
              if (!email.trim()) {
                // Synthesize a friendly validation alert instead of a
                // toast — keeps all permission/validation feedback in
                // one consistent banner inside the form.
                setLastError({
                  title: 'Email is required',
                  description:
                    'Type the email of the user you want to add or invite to this organisation.',
                  suggestion: null,
                  variant: 'warning',
                });
                return;
              }
              addMutation.mutate({
                clerkOrgId: org.orgClerkId,
                email: email.trim(),
                role: role as 'org:admin' | 'org:member',
              });
            }}
            disabled={addMutation.isPending}
          >
            {addMutation.isPending ? (
              <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
            ) : (
              <UserPlus className="mr-1.5 h-3.5 w-3.5" />
            )}
            Add or invite
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
